import React, { useState } from 'react';
import { FaIdCard, FaLock, FaUnlock, FaTrash } from 'react-icons/fa';
import { decryptData } from '../utils/encryption';

const DocumentCard = ({ data, onDelete }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [decryptedData, setDecryptedData] = useState(null);
  const [error, setError] = useState('');

  const handleCardClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!decryptedData) {
      setIsFlipped(true);
      setSecretKey('');
      setError('');
    }
  };

  const handleKeySubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Attempting to decrypt:', data.encryptedData); // Debug log
      const decrypted = await decryptData(data.encryptedData, secretKey);
      console.log('Decrypted data:', decrypted); // Debug log
      
      if (decrypted) {
        const parsedData = typeof decrypted === 'string' ? JSON.parse(decrypted) : decrypted;
        console.log('Parsed data:', parsedData); // Debug log
        setDecryptedData(parsedData);
        setError('');
      } else {
        throw new Error('Failed to decrypt data');
      }
    } catch (err) {
      console.error('Decryption error:', err);
      setError('Invalid secret key or corrupted data');
      setSecretKey('');
    }
  };

  const handleLockCard = () => {
    setIsFlipped(false);
    setDecryptedData(null);
    setSecretKey('');
    setError('');
  };

  const formatAadharNumber = (number) => {
    if (!number) return '•••• •••• ••••';
    return number.match(/.{1,4}/g)?.join(' ') || number;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDocumentFrontDisplay = () => {
    const docType = data?.category || 'DOCUMENT';
    const name = data?.title?.split(' - ')[1] || '••••••••';
    
    return (
      <>
        <div className="text-sm opacity-70 mb-1">Document Type</div>
        <div className="font-medium text-lg mb-4">{docType.replace('_', ' ')}</div>
        <div className="text-sm opacity-70 mb-1">Name</div>
        <div className="font-medium">{name}</div>
      </>
    );
  };

  const renderDocumentDetails = () => {
    if (!decryptedData) return null;

    const documentLayouts = {
      AADHAR_CARD: (
        <div className="space-y-4">
          <div>
            <div className="text-sm opacity-70 mb-1">Aadhar Number</div>
            <div className="font-mono text-lg tracking-wider">
              {formatAadharNumber(decryptedData.number)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <div className="text-sm opacity-70 mb-1">Full Name</div>
              <div className="font-medium text-lg">{decryptedData.name}</div>
            </div>
            <div className="col-span-1"> {/* Fixed grid layout */}
              <div className="text-sm opacity-70 mb-1">Date of Birth</div>
              <div className="font-medium">
                {formatDate(decryptedData.dob)}
              </div>
            </div>
            <div className="col-span-1"> {/* Fixed grid layout */}
              <div className="text-sm opacity-70 mb-1">Gender</div>
              <div className="font-medium">{decryptedData.gender}</div>
            </div>
            <div className="col-span-2">
              <div className="text-sm opacity-70 mb-1">Address</div>
              <div className="font-medium text-sm leading-relaxed">
                {decryptedData.address}
              </div>
            </div>
          </div>
        </div>
      ),
      PAN_CARD: (
        <div className="space-y-4">
          <div>
            <div className="text-sm opacity-70 mb-1">PAN Number</div>
            <div className="font-mono text-lg tracking-wider uppercase">
              {decryptedData.number}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <div className="text-sm opacity-70 mb-1">Full Name</div>
              <div className="font-medium text-lg">{decryptedData.name}</div>
            </div>
            <div className="col-span-2">
              <div className="text-sm opacity-70 mb-1">Father's Name</div>
              <div className="font-medium">{decryptedData.fatherName}</div>
            </div>
            <div className="col-span-2">
              <div className="text-sm opacity-70 mb-1">Date of Birth</div>
              <div className="font-medium">
                {formatDate(decryptedData.dob)}
              </div>
            </div>
          </div>
        </div>
      )
    };

    return documentLayouts[data.category] || (
      <div className="text-center text-gray-400">
        Unknown document type
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="w-full max-w-[420px] min-w-[300px] mx-auto aspect-[1.4/1]">
        <div className="relative w-full h-full perspective">
          <div 
            onClick={handleCardClick}
            className={`absolute inset-0 transition-transform duration-700 transform-style-3d cursor-pointer
              ${isFlipped ? 'rotate-y-180' : ''}`}
          >
            {/* Front of card */}
            <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-indigo-500 to-purple-600 
              text-white rounded-xl p-6 shadow-lg backface-hidden">
              
              {/* Document Icon and Title */}
              <div className="flex justify-between items-start mb-6">
                <div className="text-lg font-medium truncate max-w-[200px]">
                  {data?.title?.split(' - ')[0] || 'Document'}
                </div>
                <FaIdCard className="text-3xl opacity-80" />
              </div>
              {getDocumentFrontDisplay()}
            </div>

            {/* Back of card */}
            <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-gray-800 to-gray-900 
              text-white rounded-xl p-6 backface-hidden rotate-y-180">
              {!decryptedData ? (
                <form onSubmit={handleKeySubmit} className="h-full flex flex-col justify-center items-center">
                  <FaLock className="text-4xl mb-4 opacity-80" />
                  <input
                    type="password"
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder="Enter secret key"
                    className="w-full max-w-xs p-2 rounded bg-white/10 border border-white/20 
                      text-white placeholder-white/50 mb-4"
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                  {error && <div className="text-red-400 text-sm mb-4">{error}</div>}
                  <button
                    type="submit"
                    onClick={(e) => e.stopPropagation()}
                    className="px-4 py-2 bg-white/10 rounded hover:bg-white/20 transition-colors"
                  >
                    Unlock
                  </button>
                </form>
              ) : (
                <div className="h-full flex flex-col">
                  {renderDocumentDetails()}
                  
                  {/* Action Buttons */}
                  <div className="flex justify-between items-center mt-auto pt-4 border-t border-white/10">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLockCard();
                      }}
                      className="flex items-center gap-2 opacity-60 hover:opacity-100 transition-opacity"
                    >
                      <FaUnlock className="w-4 h-4" /> Lock
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(data._id);
                      }}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card Title */}
      <div className="text-center mt-3 text-gray-700 font-medium">
        {data?.title || 'Document'}
      </div>
    </div>
  );
};

export default DocumentCard;
